import { useEffect } from 'react'
import { useQueryClient } from 'react-query'

// Hooks
import {
  getAppreciationIndicatorByAddressAndPrice,
  getListingEvaluationByAddressAndPrice,
  getListingRentalPotentialByAddress,
  getMarketTempIndicatorByAddressAndPrice,
} from '../listing/requests'
import { getAccess } from '../access/requests'
import { getBranding } from '../branding/requests'
import { getChromeBotClientEvents } from '../clientEvents/requests'
import { useAuthSettings } from '../authorization'
import { useListingDisplay } from '../listing'
import { useIsExtension } from '../utility'

const useQueryPrefetches = () => {
  const queryClient = useQueryClient()
  const authSettings = useAuthSettings()
  const isChromeExtension = useIsExtension()

  const hasDefaults = Boolean(queryClient && authSettings)
  const defaultOptions = { queryClient, authSettings, isChromeExtension }

  const { address, listingPrice } = useListingDisplay()

  useAddressAndPricePrefetch(
    hasDefaults ? defaultOptions : null,
    address,
    listingPrice
  )
}

const useAddressAndPricePrefetch = (options, address, listingPrice) => {
  const { queryClient, authSettings, isChromeExtension } = options || {}

  useEffect(
    () =>
      fireAddressAndPricePrefetch(
        queryClient,
        authSettings,
        address,
        listingPrice,
        isChromeExtension
      ),
    [queryClient, authSettings, address, listingPrice, isChromeExtension]
  )
}

export const fireAddressAndPricePrefetch = (
  queryClient,
  authSettings,
  address,
  listingPrice,
  isChromeExtension
) => {
  if (queryClient && authSettings && address && listingPrice) {
    if (isChromeExtension) {
      queryClient.prefetchQuery('access', () => getAccess(authSettings))
    }
    queryClient.prefetchQuery(
      ['listingEvaluation', address, listingPrice],
      () =>
        getListingEvaluationByAddressAndPrice(
          authSettings,
          address,
          listingPrice
        )
    )
    queryClient.prefetchQuery(
      ['listingAppreciationIndicator', address, listingPrice],
      () =>
        getAppreciationIndicatorByAddressAndPrice(
          authSettings,
          address,
          listingPrice
        )
    )
    queryClient.prefetchQuery(
      ['listingMarketTempIndicator', address, listingPrice],
      () =>
        getMarketTempIndicatorByAddressAndPrice(
          authSettings,
          address,
          listingPrice
        )
    )
    queryClient.prefetchQuery(['listingRentalPotential', address], () =>
      getListingRentalPotentialByAddress(authSettings, address)
    )
    queryClient.prefetchQuery(['branding', address], () =>
      getBranding(authSettings, address)
    )
    queryClient.prefetchQuery(['clientEvents'], () =>
      getChromeBotClientEvents(authSettings)
    )
  }
}

export default useQueryPrefetches
